import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled, { keyframes } from 'styled-components';
import { getOneMember } from '../../../api/member';
import { getAllMembershipForMember } from '../../../api/memberShip';
import { getItemWithExpiration } from '../../../lib/utils';
import { MemberProfile } from '../../../services/useMemberQueryService';
import { MemberMembershipDataType } from '../../../services/useMembershipQueryService';
import { formatKoreanPhoneNumber } from '../../../utils/phoneNumber';

const pulse = keyframes`
    0%, 100% { opacity: 0.45; }
    50% { opacity: 0.9; }
`;

const Skeleton = styled.div<{ width?: string; height?: number }>`
    width: ${({ width }) => width || '100%'};
    height: ${({ height }) => height || 16}px;
    border-radius: 6px;
    background-color: #e2e8f0;
    animation: ${pulse} 1.4s ease-in-out infinite;
`;

export type MemberQuickInfoCardProps = {
    memberId: string;
    visible: boolean;
    onOpenFullProfile: () => void;
};

const queryStaleTime = 5 * 60 * 1000;

export const getActiveMemberships = (memberships: MemberMembershipDataType[] = []) =>
    memberships.filter(
        (membership) => membership.status === 'active' && !membership.isDeleted && !membership.isCanceled
    );

const MemberQuickInfoCard = ({ memberId, visible, onOpenFullProfile }: MemberQuickInfoCardProps) => {
    const authObject = getItemWithExpiration('authObject');
    const authorization = getItemWithExpiration('authorization');
    const centerId = authObject?.centerId as string | undefined;

    const memberQuery = useQuery(
        ['getCenterMemberList', centerId, memberId],
        async () => {
            const response = await getOneMember(centerId || '', memberId, authorization);
            if (response?.status !== 200 || !response?.data?.member) {
                throw new Error('회원 정보를 불러오지 못했습니다.');
            }
            return response;
        },
        {
            enabled: visible && !!centerId && !!memberId && !!authorization,
            staleTime: queryStaleTime,
            refetchOnWindowFocus: false,
        }
    );

    const memberProfile = memberQuery.data?.data?.member as MemberProfile | undefined;
    const coachId = memberProfile?.coachId;

    const membershipQuery = useQuery(
        ['getAllMembershipForMemberQuery', coachId, memberId],
        async () => {
            const response = await getAllMembershipForMember(coachId || '', memberId, authorization);
            if (response?.status !== 200) {
                throw new Error('수강권 정보를 불러오지 못했습니다.');
            }
            return response;
        },
        {
            enabled: visible && !!coachId && !!memberId && !!authorization,
            staleTime: queryStaleTime,
            refetchOnWindowFocus: false,
        }
    );

    const activeMemberships = useMemo(
        () =>
            getActiveMemberships(
                (membershipQuery.data?.data?.membersMemberships as MemberMembershipDataType[] | undefined) || []
            ),
        [membershipQuery.data]
    );

    if (!visible) return null;

    const isLoading = memberQuery.isLoading || (!!coachId && membershipQuery.isLoading);
    const isError = memberQuery.isError || membershipQuery.isError;

    const retry = () => {
        memberQuery.refetch();
        if (coachId) membershipQuery.refetch();
    };

    return (
        <section
            aria-label="회원 빠른 정보"
            style={{
                margin: '24px 72px 0',
                padding: 20,
                borderRadius: 12,
                border: '1px solid #DBEAFE',
                backgroundColor: '#F8FAFC',
            }}
        >
            {isLoading ? (
                <div aria-label="회원 정보 불러오는 중" style={{ display: 'grid', gap: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                        <Skeleton width="70%" />
                        <Skeleton width="60%" />
                    </div>
                    <Skeleton height={44} />
                    <Skeleton height={58} />
                </div>
            ) : isError ? (
                <div
                    role="alert"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}
                >
                    <span style={{ fontSize: 13, color: '#B91C1C' }}>회원 정보를 불러오지 못했습니다.</span>
                    <button
                        type="button"
                        onClick={retry}
                        style={{
                            height: 32,
                            padding: '0 14px',
                            borderRadius: 7,
                            border: '1px solid #FCA5A5',
                            backgroundColor: '#FFFFFF',
                            color: '#B91C1C',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        다시 시도
                    </button>
                </div>
            ) : memberProfile?.isDeleted || memberProfile?.isCanceled ? (
                <div role="status" style={{ color: '#64748B', fontSize: 13 }}>
                    삭제되었거나 이용이 종료된 회원의 정보는 조회할 수 없습니다.
                </div>
            ) : (
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 20,
                        }}
                    >
                        <div style={{ display: 'grid', gridTemplateColumns: '180px 180px', gap: 24 }}>
                            <InfoItem
                                label="연락처"
                                value={formatKoreanPhoneNumber(memberProfile?.phoneNumber, '-')}
                            />
                            <InfoItem label="담당 강사" value={memberProfile?.coachName || '-'} />
                        </div>
                        <button
                            type="button"
                            onClick={onOpenFullProfile}
                            style={{
                                padding: 0,
                                border: 0,
                                background: 'transparent',
                                color: '#2563EB',
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            전체 회원 정보 보기 ↗
                        </button>
                    </div>

                    <div style={{ marginTop: 18 }}>
                        <InfoItem label="회원 메모" value={memberProfile?.memo || '-'} multiline />
                    </div>

                    <div style={{ marginTop: 18 }}>
                        <span style={{ display: 'block', fontSize: 12, color: '#64748B', fontWeight: 600 }}>
                            활성 수강권
                        </span>
                        {activeMemberships.length > 0 ? (
                            <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                                {activeMemberships.map((membership) => (
                                    <div
                                        key={membership._id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 16,
                                            padding: '10px 12px',
                                            borderRadius: 8,
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                        }}
                                    >
                                        <span
                                            style={{
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: 13,
                                                color: '#0F172A',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {membership.name || '-'}
                                        </span>
                                        <span style={{ flexShrink: 0, fontSize: 12, color: '#475569', fontWeight: 600 }}>
                                            {membership.currentSession ?? 0}/{membership.totalSession ?? 0}회 · 잔여{' '}
                                            {membership.remainSession ?? 0}회
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: '12px',
                                    borderRadius: 8,
                                    backgroundColor: '#FFFFFF',
                                    color: '#94A3B8',
                                    fontSize: 13,
                                }}
                            >
                                활성 수강권이 없습니다.
                            </div>
                        )}
                    </div>
                </>
            )}
        </section>
    );
};

const InfoItem = ({ label, value, multiline = false }: { label: string; value: string; multiline?: boolean }) => (
    <div style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, color: '#64748B', fontWeight: 600 }}>{label}</span>
        <span
            style={{
                display: multiline ? '-webkit-box' : 'block',
                marginTop: 5,
                color: '#0F172A',
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.5,
                whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: multiline ? 3 : undefined,
                WebkitBoxOrient: multiline ? 'vertical' : undefined,
            }}
        >
            {value}
        </span>
    </div>
);

export default MemberQuickInfoCard;
